<template>
  <div class="delivery-task-list">
    <!-- 页面头部 -->
    <div class="page-header">
      <button class="btn-back" @click="$router.back()">←</button>
      <h1>{{ $t('wholesaler.DeliveryTaskList.line6_text') }}</h1>
      <div style="width: 32px;"></div>
    </div>

    <!-- 快捷操作栏 -->
    <div class="quick-actions">
      <button class="action-btn" @click="$router.push('/wholesaler/order-on-behalf')">
        <div class="action-icon">🛒</div>
        <div class="action-text">{{ $t('wholesaler.DeliveryTaskList.line14_text') }}</div>
      </button>
      <button class="action-btn" @click="$router.push('/wholesaler/orders')">
        <div class="action-icon">🔍</div>
        <div class="action-text">{{ $t('wholesaler.DeliveryTaskList.line18_text') }}</div>
      </button>
      <button class="action-btn" @click="$router.push('/wholesaler/driver-management')">
        <div class="action-icon">👥</div>
        <div class="action-text">{{ $t('wholesaler.DeliveryTaskList.line22_text') }}</div>
      </button>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-bar" v-if="!loading">
      <div class="stat-item" :class="{ active: filterStatus === '' }" @click="setFilterStatus('')">
        <span class="stat-num">{{ stats.total }}</span>
        <span class="stat-label">{{ $t('wholesaler.DeliveryTaskList.line30_text') }}</span>
      </div>
      <div class="stat-item stat-pending" :class="{ active: filterStatus === 'PENDING' }" @click="setFilterStatus('PENDING')">
        <span class="stat-num">{{ stats.pending }}</span>
        <span class="stat-label">{{ $t('wholesaler.DeliveryTaskList.line34_text') }}</span>
      </div>
      <div class="stat-item stat-waiting" :class="{ active: filterStatus === 'WAITING_FOR_DRIVER' }" @click="setFilterStatus('WAITING_FOR_DRIVER')">
        <span class="stat-num">{{ stats.waiting }}</span>
        <span class="stat-label">{{ $t('wholesaler.DeliveryTaskList.line38_text') }}</span>
      </div>
      <div class="stat-item stat-delivering" :class="{ active: filterStatus === 'DELIVERING' }" @click="setFilterStatus('DELIVERING')">
        <span class="stat-num">{{ stats.delivering }}</span>
        <span class="stat-label">{{ $t('wholesaler.DeliveryTaskList.line42_text') }}</span>
      </div>
      <div class="stat-item stat-completed" :class="{ active: filterStatus === 'COMPLETED' }" @click="setFilterStatus('COMPLETED')">
        <span class="stat-num">{{ stats.completed }}</span>
        <span class="stat-label">{{ $t('wholesaler.DeliveryTaskList.line46_text') }}</span>
      </div>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
        v-model="searchKeyword"
        :placeholder="$t('wholesaler.DeliveryTaskList.line54_placeholder')"
        prefix-icon="el-icon-search"
        clearable
        @input="handleSearch"
      />
    </div>

    <!-- 任务列表 -->
    <div class="task-list">
      <div v-if="loading" class="loading-state">
        <div class="loading-icon">⏳</div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="tasks.length === 0" class="empty-state">
        <div class="empty-icon">📦</div>
        <p>{{ $t('wholesaler.DeliveryTaskList.line70_text') }}</p>
      </div>

      <div v-else>
        <div v-for="task in tasks" :key="task.id" class="task-card">
          <!-- 任务头部 -->
          <div class="task-header">
            <div class="task-info">
              <span class="task-no">{{ task.taskNo }}</span>
              <el-tag :type="getTaskTypeTag(task.taskType)" size="mini">
                {{ getTaskTypeText(task.taskType) }}
              </el-tag>
              <el-tag v-if="task.afterSalesId" size="mini" type="warning">{{ $t('wholesaler.DeliveryTaskList.line82_text') }}</el-tag>
            </div>
            <el-tag :type="getStatusTag(task.status)" size="small">
              {{ getStatusText(task.status) }}
            </el-tag>
          </div>

          <!-- 任务内容 -->
          <div class="task-body">
            <!-- 配送员信息 -->
            <div class="info-row" v-if="task.driverName">
              <span class="label">{{ $t('wholesaler.DeliveryTaskList.line93_text') }}</span>
              <span class="value">{{ task.driverName }} (ID: {{ task.driverId }})</span>
            </div>
            <div class="info-row" v-else>
              <span class="label">{{ $t('wholesaler.DeliveryTaskList.line97_text') }}</span>
              <span class="value text-muted">{{ $t('wholesaler.DeliveryTaskList.line98_text') }}</span>
            </div>

            <!-- 客户信息 -->
            <div class="info-row">
              <span class="label">{{ $t('wholesaler.DeliveryTaskList.line103_text') }}</span>
              <span class="value">{{ task.customerName || '-' }}</span>
            </div>

            <!-- 联系人电话 -->
            <div class="info-row" v-if="task.deliveryContact || task.deliveryPhone">
              <span class="label">{{ $t('wholesaler.DeliveryTaskList.line109_text') }}</span>
              <span class="value">
                {{ task.deliveryContact || '' }} {{ task.deliveryPhone || '' }}
                <el-button size="mini" type="primary" icon="el-icon-phone" @click.stop="callCustomer(task)" v-if="task.deliveryPhone">{{ $t('wholesaler.DeliveryTaskList.line112_text') }}</el-button>
              </span>
            </div>

            <!-- 取货地址 -->
            <div class="info-row">
              <span class="label">{{ $t('wholesaler.DeliveryTaskList.line118_text') }}</span>
              <span class="value">
                {{ task.pickupAddress || '-' }}
                <el-button size="mini" @click.stop="navigateTo(task, 'pickup')">{{ $t('wholesaler.DeliveryTaskList.line121_text') }}</el-button>
              </span>
            </div>

            <!-- 送货地址 -->
            <div class="info-row">
              <span class="label">{{ $t('wholesaler.DeliveryTaskList.line127_text') }}</span>
              <span class="value">
                {{ task.deliveryAddress || '-' }}
                <el-button size="mini" @click.stop="navigateTo(task, 'delivery')">{{ $t('wholesaler.DeliveryTaskList.line130_text') }}</el-button>
              </span>
            </div>

            <!-- 配送商品列表 -->
            <div class="items-section" v-if="task.orderItems && task.orderItems.length > 0">
              <div class="items-header">{{ $t('wholesaler.DeliveryTaskList.deliveryItems') }} ({{ task.orderItems.length }})</div>
              <div class="item-row" v-for="item in task.orderItems" :key="item.productId">
                <img v-if="item.productImage" :src="getImageUrl(item.productImage)" class="item-img" :alt="$t('wholesaler.DeliveryTaskList.line138_alt')" />
                <div v-else class="item-img item-img-placeholder">📦</div>
                <div class="item-info">
                  <div class="item-name">{{ item.productName }}</div>
                  <div class="item-meta">
                    <span v-if="item.productSku" class="item-sku">{{ item.productSku }}</span>
                    <span class="item-qty">× {{ formatQuantity(item.quantity) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 时间信息 -->
            <div class="info-row time-row">
              <span class="label">{{ $t('wholesaler.DeliveryTaskList.line152_text') }}</span>
              <span class="value">{{ task.createdAt || '-' }}</span>
            </div>
            <div class="info-row time-row" v-if="task.pickedUpAt">
              <span class="label">{{ $t('wholesaler.DeliveryTaskList.line156_text') }}</span>
              <span class="value">{{ task.pickedUpAt }}</span>
            </div>
            <div class="info-row time-row" v-if="task.completedAt">
              <span class="label">{{ $t('wholesaler.DeliveryTaskList.line160_text') }}</span>
              <span class="value">{{ task.completedAt }}</span>
            </div>

            <!-- COD 提示 -->
            <div class="cod-alert" v-if="task.paymentMethod === 'CASH' && !task.codCollected">
              💰 {{ $t('wholesaler.DeliveryTaskList.cod') }}: S$ {{ task.totalAmount }} - {{ $t('wholesaler.DeliveryTaskList.codCollectHint') }}
            </div>
            <div class="cod-collected" v-if="task.codCollected">
              ✅ {{ $t('wholesaler.DeliveryTaskList.codCollected') }}
            </div>

            <!-- 备注 -->
            <div class="info-row" v-if="task.remark">
              <span class="label">{{ $t('wholesaler.DeliveryTaskList.line174_text') }}</span>
              <span class="value text-muted">{{ task.remark }}</span>
            </div>
          </div>

          <!-- 任务操作按钮 -->
          <div class="task-actions">
            <el-button size="mini" type="info" @click.stop="viewOrderDetail(task)" :disabled="!task.orderId">
              📋 {{ $t('wholesaler.DeliveryTaskList.viewOrder') }}
            </el-button>
            <el-button size="mini" @click.stop="printOrder(task)" :disabled="!task.orderId">
              🖨️ {{ $t('wholesaler.DeliveryTaskList.printReceipt') }}
            </el-button>
            <el-button size="mini" type="success" @click.stop="pickupTask(task)" v-if="task.status === 'PENDING'">
              ✅ {{ $t('wholesaler.DeliveryTaskList.confirmPickup') }}
            </el-button>
            <el-button size="mini" type="success" @click.stop="completeTask(task)" v-if="task.status === 'DELIVERING'">
              ✅ {{ $t('wholesaler.DeliveryTaskList.confirmDelivered') }}
            </el-button>
            <el-button size="mini" type="warning" @click.stop="showCODDialog(task)" 
              v-if="task.paymentMethod === 'CASH' && !task.codCollected && (task.status === 'DELIVERING' || task.status === 'COMPLETED')">
              💰 {{ $t('wholesaler.DeliveryTaskList.codReceipt') }}
            </el-button>
            <el-button size="mini" type="danger" @click.stop="cancelTask(task)" v-if="canCancel(task)">
              ❌ {{ $t('common.cancel') }}
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination" v-if="total > pageSize">
      <el-pagination
        background
        layout="prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        @current-change="handlePageChange"
      />
    </div>

    <!-- COD 收款弹窗 -->
    <el-dialog :title="$t('wholesaler.DeliveryTaskList.line218_title')" :visible.sync="codDialogVisible" width="400px" :close-on-click-modal="false">
      <div v-if="codDialogTask" class="cod-dialog-content">
        <div class="cod-order-info">
          <div>{{ $t('wholesaler.DeliveryTaskList.orderNo') }}: {{ codDialogTask.orderNo || '-' }}</div>
          <div>{{ $t('wholesaler.DeliveryTaskList.customerLabel') }}: {{ codDialogTask.customerName || '-' }}</div>
          <div class="cod-amount">{{ $t('wholesaler.DeliveryTaskList.line223_text') }} <strong>S$ {{ codDialogTask.totalAmount }}</strong></div>
        </div>
        <el-divider>{{ $t('wholesaler.DeliveryTaskList.line225_text') }}</el-divider>
        <el-input-number v-model="codForm.amount" :precision="2" :min="0" :max="codDialogTask.totalAmount" size="large" style="width:200px;" />
        <el-divider>{{ $t('wholesaler.DeliveryTaskList.line227_text') }}</el-divider>
        <el-radio-group v-model="codForm.paymentMethod" class="cod-radio-group">
          <el-radio-button label="CASH">{{ $t('wholesaler.DeliveryTaskList.line229_text') }}</el-radio-button>
          <el-radio-button label="PAYNOW">📱 {{ $t('wholesaler.DeliveryTaskList.paynow') }}</el-radio-button>
          <el-radio-button label="CREDIT_CARD">{{ $t('wholesaler.DeliveryTaskList.line231_text') }}</el-radio-button>
        </el-radio-group>
        <el-divider>{{ $t('wholesaler.DeliveryTaskList.line233_text') }}</el-divider>
        <div class="cod-upload-area">
          <el-upload
            :action="uploadUrl"
            :headers="uploadHeaders"
            :on-success="handleCODUploadSuccess"
            :on-error="handleCODUploadError"
            :before-upload="beforeUpload"
            :file-list="codFileList"
            list-type="picture-card"
            :limit="3"
            accept="image/*"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
        </div>
        <div class="cod-hint">{{ $t('wholesaler.DeliveryTaskList.line249_text') }}</div>
      </div>
      <div slot="footer">
        <el-button @click="codDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="warning" :loading="codDialogLoading" @click="submitCOD">{{ $t('wholesaler.DeliveryTaskList.line253_text') }}</el-button>
      </div>
    </el-dialog>

    <!-- 打印组件（隐藏） -->
    <bluetooth-print-button
      ref="printBtn"
      button-type="success"
      button-size="mini"
      :disabled="!selectedTask"
      print-type="order"
      :order-data="orderData"
      style="display: none"
    />

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="goToPage('home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('common.home') }}</span>
      </button>
      <button class="nav-item" @click="goToPage('products')">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('common.products') }}</span>
      </button>
      <button class="nav-item" @click="goToPage('wholesale')">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('wholesaler.DeliveryTaskList.line280_text') }}</span>
      </button>
      <button class="nav-item" @click="goToPage('orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('common.orders') }}</span>
      </button>
      <button class="nav-item" @click="goToPage('profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('common.profile') }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { getTasksByWholesaler, cancelTask, updateTaskStatus, collectCOD } from '@/api/delivery'
import api from '@/services/api'
import BluetoothPrintButton from '@/components/BluetoothPrintButton.vue'
import { getImageUrl } from '@/utils/image'

export default {
  name: 'DeliveryTaskList',
  components: {
    BluetoothPrintButton
  },
  data() {
    return {
      tasks: [],
      loading: false,
      searchKeyword: '',
      filterStatus: '',
      currentPage: 1,
      pageSize: 20,
      total: 0,
      stats: {
        total: 0,
        pending: 0,
        waiting: 0,
        delivering: 0,
        completed: 0
      },
      // 打印相关
      selectedTask: null,
      orderData: null,
      // COD 收款弹窗
      codDialogVisible: false,
      codDialogTask: null,
      codDialogLoading: false,
      codForm: { amount: 0, proofUrl: '', paymentMethod: 'CASH' },
      codFileList: []
    }
  },
  computed: {
    uploadUrl() {
      return (process.env.VUE_APP_API_URL || '/api') + '/files/upload-image'
    },
    uploadHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('auth_token') }
    }
  },
  created() {
    this.loadTasks()
  },
  methods: {
    getImageUrl,
    /**
     * 加载配送任务列表
     */
    async loadTasks() {
      this.loading = true
      try {
        const wholesalerId = localStorage.getItem('stored_wholesaler_id') || 1
        const params = {
          wholesalerId,
          page: this.currentPage - 1,
          size: this.pageSize
        }
        if (this.searchKeyword) {
          params.keyword = this.searchKeyword
        }
        if (this.filterStatus) {
          params.status = this.filterStatus
        }

        const res = await getTasksByWholesaler(params)
        this.tasks = res.content || []
        this.total = res.total || 0
        this.updateStats()
      } catch (e) {
        this.$message.error(this.$t('wholesaler.DeliveryTaskList.loadFailed') + ': ' + (e.message || e))
      } finally {
        this.loading = false
      }
    },

    /**
     * 更新统计数据
     */
    async updateStats() {
      const wholesalerId = localStorage.getItem('stored_wholesaler_id') || 1
      try {
        const allTasks = this.tasks
        this.stats.total = this.total
        this.stats.pending = allTasks.filter(t => t.status === 'PENDING').length
        this.stats.waiting = allTasks.filter(t => t.status === 'WAITING_FOR_DRIVER').length
        this.stats.delivering = allTasks.filter(t => t.status === 'DELIVERING').length
        this.stats.completed = allTasks.filter(t => t.status === 'COMPLETED').length
      } catch (e) {
        console.error('更新统计数据失败', e)
      }
    },

    /**
     * 设置筛选状态
     */
    setFilterStatus(status) {
      this.filterStatus = status
      this.currentPage = 1
      this.loadTasks()
    },

    /**
     * 格式化数量
     */
    formatQuantity(qty) {
      if (qty == null) return '0'
      const num = parseFloat(qty)
      return Number.isInteger(num) ? num.toString() : num.toFixed(2)
    },

    /**
     * 搜索
     */
    handleSearch() {
      this.currentPage = 1
      this.loadTasks()
    },

    /**
     * 分页切换
     */
    handlePageChange(page) {
      this.currentPage = page
      this.loadTasks()
    },

    /**
     * 查看订单详情
     */
    viewOrderDetail(task) {
      this.$router.push(`/wholesaler/orders/${task.orderId}`)
    },

    /**
     * 拨打电话
     */
    callCustomer(task) {
      const phone = task.deliveryPhone || task.pickupPhone
      if (phone) {
        window.location.href = `tel:${phone}`
      } else {
        this.$message.warning(this.$t('wholesaler.DeliveryTaskList.noPhone'))
      }
    },

    /**
     * 导航
     */
    navigateTo(task, type) {
      const isPickup = type === 'pickup'
      const lat = isPickup ? task.pickupGpsLat : task.deliveryGpsLat
      const lng = isPickup ? task.pickupGpsLng : task.deliveryGpsLng
      const addr = isPickup ? (task.pickupAddress || '仓库') : (task.deliveryAddress || '-')
      
      if (lat && lng) {
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank')
      } else if (addr) {
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`, '_blank')
      }
    },

    /**
     * 打印小票
     */
    async printOrder(task) {
      this.selectedTask = task
      if (task.orderId) {
        try {
          const res = await api.get(`/orders/${task.orderId}`)
          this.orderData = res || res.data
          this.$nextTick(() => {
            const btn = this.$refs.printBtn
            if (btn) btn.handlePrint()
          })
        } catch (e) {
          this.$message.error(this.$t('wholesaler.DeliveryTaskList.loadOrderFailed'))
        }
      } else {
        this.$message.warning(this.$t('wholesaler.DeliveryTaskList.noLinkedOrder'))
      }
    },

    /**
     * 确认取货
     */
    async pickupTask(task) {
      try {
        await this.$confirm(this.$t('wholesaler.DeliveryTaskList.confirmPickupMsg'), this.$t('wholesaler.DeliveryTaskList.confirmPickup'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'info'
        })

        const driverId = task.driverId
        await api.post(`/delivery/tasks/${task.id}/pickup`, null, {
          params: { driverId }
        })
        
        this.$message.success(this.$t('wholesaler.DeliveryTaskList.pickupSuccess'))
        this.loadTasks()
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('操作失败: ' + (e.message || e))
        }
      }
    },

    /**
     * 确认送达
     */
    async completeTask(task) {
      try {
        await this.$confirm(this.$t('wholesaler.DeliveryTaskList.confirmDeliveredMsg'), this.$t('wholesaler.DeliveryTaskList.confirmDelivered'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'success'
        })

        await updateTaskStatus(task.id, 'COMPLETED')
        
        this.$message.success(this.$t('wholesaler.DeliveryTaskList.deliveredSuccess'))
        this.loadTasks()
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('操作失败: ' + (e.message || e))
        }
      }
    },

    /**
     * 显示 COD 收款弹窗
     */
    showCODDialog(task) {
      this.codDialogTask = task
      this.codForm.amount = task.totalAmount || 0
      this.codForm.proofUrl = ''
      this.codForm.paymentMethod = 'CASH'
      this.codFileList = []
      this.codDialogVisible = true
    },

    /**
     * 提交 COD 收款
     */
    async submitCOD() {
      if (!this.codDialogTask) return
      if (!this.codForm.proofUrl) {
        this.$message.warning(this.$t('wholesaler.DeliveryTaskList.uploadProofHint'))
        return
      }
      try {
        this.codDialogLoading = true
        await collectCOD(this.codDialogTask.id, {
          driverId: this.codDialogTask.driverId,
          amount: this.codForm.amount,
          proofUrl: this.codForm.proofUrl,
          paymentMethod: this.codForm.paymentMethod
        })
        this.$message.success(this.$t('wholesaler.DeliveryTaskList.receiptSuccess'))
        this.codDialogVisible = false
        this.loadTasks()
      } catch (e) {
        this.$message.error(this.$t('wholesaler.DeliveryTaskList.receiptFailed') + ': ' + (e.message || e))
      } finally {
        this.codDialogLoading = false
      }
    },

    /**
     * COD 上传成功
     */
    handleCODUploadSuccess(response) {
      if (response.url) {
        this.codForm.proofUrl = response.url
        this.$message.success(this.$t('common.uploadSuccess'))
      }
    },

    /**
     * COD 上传失败
     */
    handleCODUploadError() {
      this.$message.error(this.$t('common.uploadFailed'))
    },

    /**
     * 上传前检查
     */
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        this.$message.error(this.$t('wholesaler.DeliveryTaskList.onlyImage'))
      }
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        this.$message.error(this.$t('wholesaler.DeliveryTaskList.imageMax5MB'))
      }
      return isImage && isLt5M
    },

    /**
     * 取消任务
     */
    async cancelTask(task) {
      try {
        await this.$confirm(this.$t('wholesaler.DeliveryTaskList.confirmCancelMsg'), this.$t('wholesaler.DeliveryTaskList.cancelTask'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })

        await cancelTask(task.id, '批发商取消')
        this.$message.success(this.$t('wholesaler.DeliveryTaskList.cancelSuccess'))
        this.loadTasks()
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(this.$t('wholesaler.DeliveryTaskList.cancelFailed') + ': ' + (e.message || e))
        }
      }
    },

    /**
     * 是否可以取消
     */
    canCancel(task) {
      return task.status !== 'COMPLETED' && task.status !== 'CANCELLED'
    },

    /**
     * 获取任务类型标签
     */
    getTaskTypeTag(type) {
      const map = {
        ASSIGNED: '',
        CONTRACTOR: 'warning',
        GRABBED: 'success'
      }
      return map[type] || 'info'
    },

    /**
     * 获取任务类型文本
     */
    getTaskTypeText(type) {
      const map = {
        ASSIGNED: this.$t('wholesaler.DeliveryTaskList.typeAssigned'),
        CONTRACTOR: this.$t('wholesaler.DeliveryTaskList.typeContractor'),
        GRABBED: this.$t('wholesaler.DeliveryTaskList.typeGrabbed')
      }
      return map[type] || type
    },

    /**
     * 获取状态标签
     */
    getStatusTag(status) {
      const map = {
        PENDING: 'warning',
        WAITING_FOR_DRIVER: 'info',
        DELIVERING: '',
        COMPLETED: 'success',
        CANCELLED: 'danger'
      }
      return map[status] || 'info'
    },

    /**
     * 获取状态文本
     */
    getStatusText(status) {
      const map = {
        PENDING: this.$t('wholesaler.DeliveryTaskList.statusPending'),
        WAITING_FOR_DRIVER: this.$t('wholesaler.DeliveryTaskList.statusWaiting'),
        DELIVERING: this.$t('wholesaler.DeliveryTaskList.statusDelivering'),
        COMPLETED: this.$t('wholesaler.DeliveryTaskList.statusCompleted'),
        CANCELLED: this.$t('wholesaler.DeliveryTaskList.statusCancelled')
      }
      return map[status] || status
    },

    /**
     * 底部导航跳转
     */
    goToPage(tab) {
      const routes = {
        home: '/wholesaler/home',
        products: '/wholesaler/product',
        wholesale: '/wholesaler/wholesale-price-list',
        orders: '/wholesaler/orders',
        profile: '/my-profile'
      }
      if (routes[tab]) {
        this.$router.push(routes[tab])
      }
    }
  }
}
</script>

<style scoped lang="scss">
.delivery-task-list {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 70px;
}

/* ========== 底部导航 ========== */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

/* ========== 页面头部 ========== */
.page-header {
  background: #fff;
  padding: calc(16px + env(safe-area-inset-top, 0px));
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  h1 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #333;
  }
  
  .btn-back {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    padding: 8px;
  }
}

/* ========== 快捷操作栏 ========== */
.quick-actions {
  display: flex;
  background: #fff;
  padding: 12px 16px;
  gap: 12px;
  border-bottom: 1px solid #e8e8e8;
  
  .action-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    border-radius: 8px;
    transition: background 0.2s;
    
    &:active {
      background: #f5f5f5;
    }
    
    .action-icon {
      font-size: 24px;
    }
    
    .action-text {
      font-size: 12px;
      color: #666;
    }
  }
}

/* ========== 统计卡片 ========== */
.stats-bar {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  
  .stat-item {
    flex: 1;
    text-align: center;
    padding: 8px 4px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    
    &.active {
      background: #f0f0f0;
    }
    
    &.stat-pending.active {
      background: #FFF3E0;
    }
    
    &.stat-waiting.active {
      background: #F3E5F5;
    }
    
    &.stat-delivering.active {
      background: #E3F2FD;
    }
    
    &.stat-completed.active {
      background: #E8F5E9;
    }
    
    .stat-num {
      display: block;
      font-size: 18px;
      font-weight: 700;
      color: #666;
    }
    
    .stat-label {
      font-size: 11px;
      color: #999;
    }
    
    &.stat-pending .stat-num {
      color: #FF6B35;
    }
    
    &.stat-waiting .stat-num {
      color: #9C27B0;
    }
    
    &.stat-delivering .stat-num {
      color: #1976D2;
    }
    
    &.stat-completed .stat-num {
      color: #388E3C;
    }
  }
}

/* ========== 搜索栏 ========== */
.search-bar {
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
}

/* ========== 商品列表 ========== */
.items-section {
  margin: 8px 0;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 8px;
  
  .items-header {
    font-size: 12px;
    font-weight: 600;
    color: #666;
    margin-bottom: 8px;
  }
  
  .item-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    
    &:not(:last-child) {
      border-bottom: 1px solid #f0f0f0;
    }
    
    .item-img,
    .item-img-placeholder {
      width: 40px;
      height: 40px;
      border-radius: 6px;
      object-fit: cover;
      flex-shrink: 0;
    }
    
    .item-img-placeholder {
      background: #f0f0f0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
    }
    
    .item-info {
      flex: 1;
      min-width: 0;
      
      .item-name {
        font-size: 13px;
        color: #333;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .item-meta {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 2px;
        font-size: 12px;
        color: #999;
        
        .item-sku {
          font-family: monospace;
          background: #f0f0f0;
          padding: 1px 4px;
          border-radius: 3px;
        }
      }
    }
  }
}

/* ========== 任务列表 ========== */
.task-list {
  padding: 12px 16px;
  
  .loading-state,
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    
    .loading-icon,
    .empty-icon {
      font-size: 48px;
      margin-bottom: 12px;
    }
    
    p {
      margin: 0;
      color: #999;
      font-size: 14px;
    }
  }
  
  .task-card {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    
    .task-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      background: #f9f9f9;
      border-bottom: 1px solid #f0f0f0;
      
      .task-info {
        display: flex;
        align-items: center;
        gap: 8px;
        
        .task-no {
          font-size: 13px;
          font-weight: 600;
          color: #333;
          font-family: monospace;
        }
      }
    }
    
    .task-body {
      padding: 12px 16px;
      
      .info-row {
        display: flex;
        gap: 8px;
        margin-bottom: 8px;
        font-size: 13px;
        align-items: center;
        
        &.time-row {
          font-size: 12px;
          color: #999;
          
          .label, .value {
            color: #999;
          }
        }
        
        .label {
          flex-shrink: 0;
          color: #666;
        }
        
        .value {
          flex: 1;
          color: #333;
          word-break: break-all;
          display: flex;
          align-items: center;
          gap: 8px;
          
          &.text-muted {
            color: #999;
          }
        }
        
        &:last-child {
          margin-bottom: 0;
        }
      }
      
      .cod-alert {
        margin: 12px 0;
        padding: 10px 14px;
        background: #FFF8E1;
        border-left: 4px solid #FF9800;
        border-radius: 4px;
        font-size: 14px;
        color: #E65100;
        font-weight: 600;
      }
      
      .cod-collected {
        margin: 12px 0;
        padding: 10px 14px;
        background: #E8F5E9;
        border-left: 4px solid #4CAF50;
        border-radius: 4px;
        font-size: 14px;
        color: #2E7D32;
        font-weight: 600;
      }
    }
    
    .task-actions {
      display: flex;
      gap: 8px;
      padding: 8px 16px 12px;
      flex-wrap: wrap;
      border-top: 1px solid #f0f0f0;
    }
  }
}

/* ========== 分页 ========== */
.pagination {
  display: flex;
  justify-content: center;
  padding: 20px 16px;
}

/* ========== COD 弹窗 ========== */
.cod-dialog-content {
  .cod-order-info {
    padding: 12px;
    background: #f5f5f5;
    border-radius: 8px;
    margin-bottom: 12px;
    .cod-amount {
      margin-top: 8px;
      font-size: 16px;
      strong { color: #E65100; }
    }
  }
  .cod-hint {
    margin-top: 12px;
    font-size: 12px;
    color: #999;
  }
  .cod-radio-group {
    width: 100%;
    display: flex;
    .el-radio-button {
      flex: 1;
      .el-radio-button__inner {
        width: 100%;
      }
    }
  }
  .cod-upload-area {
    .el-upload--picture-card {
      width: 80px;
      height: 80px;
      line-height: 80px;
    }
    .el-upload-list__item {
      width: 80px;
      height: 80px;
    }
  }
}
</style>
